import { createFileRoute, useParams } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { PortalShell } from "@/components/portal/PortalShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  generateDocument,
  listProjectDocuments,
  decideDocument,
} from "@/lib/document-generator.functions";
import {
  renderDocumentVersion,
  signDocumentVersion,
  listDocumentVersions,
} from "@/lib/document-export.functions";
import { listQuotes, createQuote, buildQuoteEkdComparison } from "@/lib/quote-engine.functions";

export const Route = createFileRoute("/_authenticated/app/projects/$id/documents")({
  component: DocumentsPage,
  errorComponent: ({ error }) => <div className="p-6 text-sm text-destructive">{error.message}</div>,
  notFoundComponent: () => <div className="p-6 text-sm">Nicht gefunden.</div>,
});

const PHASE1_DOC_TYPES = [
  { key: "project_description", label: "Projektbeschreibung" },
  { key: "overview", label: "Gesamtübersicht" },
  { key: "subsidy_report", label: "Förderbericht" },
  { key: "tax_report", label: "Steuerbericht" },
  { key: "economic_preview", label: "Wirtschaftlichkeitsvorschau" },
  { key: "variant_comparison", label: "Variantenvergleich" },
  { key: "project_plan", label: "Projektplan" },
  { key: "quote_draft", label: "Angebotsentwurf (Phase 1)" },
] as const;

function DocumentsPage() {
  const { id } = useParams({ from: "/_authenticated/app/projects/$id/documents" });
  const qc = useQueryClient();
  const list = useServerFn(listProjectDocuments);
  const gen = useServerFn(generateDocument);
  const render = useServerFn(renderDocumentVersion);
  const sign = useServerFn(signDocumentVersion);
  const decide = useServerFn(decideDocument);
  const listQ = useServerFn(listQuotes);
  const createQ = useServerFn(createQuote);
  const ekd = useServerFn(buildQuoteEkdComparison);

  const docs = useQuery({ queryKey: ["docs", id], queryFn: () => list({ data: { projectId: id } }) });
  const quotes = useQuery({ queryKey: ["quotes", id], queryFn: () => listQ({ data: { projectId: id } }) });

  const mGen = useMutation({
    mutationFn: (docType: string) => gen({ data: { projectId: id, docType: docType as never } }),
    onSuccess: () => {
      toast.success("Phase-1-Dokument erstellt");
      qc.invalidateQueries({ queryKey: ["docs", id] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const mRender = useMutation({
    mutationFn: ({ documentId, format }: { documentId: string; format: "pdf" | "docx" | "xlsx" }) =>
      render({ data: { documentId, format } }),
    onSuccess: () => {
      toast.success("Version gerendert");
      qc.invalidateQueries({ queryKey: ["docs", id] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <PortalShell nav={[{ to: "/app/portal", label: "Übersicht" }, { to: `/app/projects/${id}/uploads`, label: "Uploads" }]}>
      <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Dokumente & Angebote</h1>
        <p className="text-sm text-muted-foreground">Phase 1 unverbindlich · Phase 2 nach Freigabe</p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Phase-1-Dokumente generieren</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {PHASE1_DOC_TYPES.map((t) => (
            <Button key={t.key} variant="outline" size="sm" disabled={mGen.isPending} onClick={() => mGen.mutate(t.key)}>
              + {t.label}
            </Button>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Vorhandene Dokumente</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {(docs.data?.documents ?? []).map((d: any) => (
            <DocRow
              key={d.id}
              doc={d}
              onRender={(f) => mRender.mutate({ documentId: d.id, format: f })}
              onSign={async (versionId) => {
                const r = await sign({ data: { versionId } });
                window.open(r.url, "_blank");
              }}
              onDecide={async (decision, visible) => {
                await decide({ data: { documentId: d.id, decision, visibleToCustomer: visible } });
                qc.invalidateQueries({ queryKey: ["docs", id] });
              }}
            />
          ))}
          {(docs.data?.documents ?? []).length === 0 && (
            <p className="text-sm text-muted-foreground">Noch keine Dokumente.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Angebote</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button
            size="sm"
            onClick={async () => {
              await createQ({
                data: { projectId: id, phase: "phase1_standard", title: "Phase-1-Angebotsentwurf" },
              });
              qc.invalidateQueries({ queryKey: ["quotes", id] });
            }}
          >
            + Phase-1-Angebotsentwurf
          </Button>
          {(quotes.data?.quotes ?? []).map((q: any) => (
            <div key={q.id} className="flex items-center justify-between rounded border p-3 text-sm">
              <div>
                <div className="font-medium">{q.quote_no} · {q.title}</div>
                <div className="text-xs text-muted-foreground">
                  <Badge variant="outline" className="mr-1">{q.phase}</Badge>
                  <Badge variant="secondary">{q.status}</Badge>
                  · {Number(q.total_gross ?? 0).toFixed(2)} {q.currency}
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  await ekd({ data: { quoteId: q.id } });
                  toast.success("EKD-Vergleich aktualisiert");
                  qc.invalidateQueries({ queryKey: ["quotes", id] });
                }}
              >
                EKD-Vergleich
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>
      </div>
    </PortalShell>
  );
}

function DocRow({
  doc,
  onRender,
  onSign,
  onDecide,
}: {
  doc: any;
  onRender: (f: "pdf" | "docx" | "xlsx") => void;
  onSign: (versionId: string) => void;
  onDecide: (decision: "approved" | "rejected", visible: boolean) => void;
}) {
  const listV = useServerFn(listDocumentVersions);
  const [open, setOpen] = useState(false);
  const versions = useQuery({
    queryKey: ["versions", doc.id, open],
    queryFn: () => listV({ data: { documentId: doc.id } }),
    enabled: open,
  });
  return (
    <div className="rounded border p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-medium">{doc.title}</div>
          <div className="text-xs text-muted-foreground">
            <Badge variant="outline" className="mr-1">{doc.phase}</Badge>
            <Badge variant="secondary">{doc.status}</Badge>
            · v{doc.current_version}
          </div>
          {doc.disclaimer && <p className="mt-1 text-xs italic text-amber-700">{doc.disclaimer}</p>}
        </div>
        <div className="flex flex-wrap gap-1">
          <Button size="sm" variant="outline" onClick={() => onRender("pdf")}>PDF</Button>
          <Button size="sm" variant="outline" onClick={() => onRender("docx")}>Word</Button>
          <Button size="sm" variant="outline" onClick={() => onRender("xlsx")}>Excel</Button>
          <Button size="sm" variant="outline" onClick={() => setOpen((v) => !v)}>Versionen</Button>
          <Button size="sm" onClick={() => onDecide("approved", true)}>Freigeben</Button>
          <Button size="sm" variant="destructive" onClick={() => onDecide("rejected", false)}>Ablehnen</Button>
        </div>
      </div>
      {open && (
        <div className="mt-3 space-y-1 text-xs">
          {(versions.data?.versions ?? []).map((v: any) => (
            <div key={v.id} className="flex items-center justify-between rounded border px-2 py-1">
              <span>v{v.version} · {v.format} · {v.size_bytes ?? "—"} B</span>
              {v.path && (
                <Button size="sm" variant="link" onClick={() => onSign(v.id)}>
                  Download
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
